import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { BanknotesIcon } from '@heroicons/react/24/outline';

interface ManualPaymentOrder {
  _id: string;
  userId: { _id: string; name?: string; email?: string };
  totalPriceEGP: number;
  paymentMethod: string;
  paymentStatus: string;
  paymentScreenshotUrl?: string;
  paymentRejectionReason?: string;
  createdAt: string;
  status: string;
}

const PAYMENT_STATUS_KEYS: Record<string, string> = {
  pending_upload: 'pendingUpload',
  awaiting_admin_review: 'awaitingReview',
  approved: 'approved',
  rejected: 'rejected',
};

export default function ManualPaymentsTable() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [orders, setOrders] = useState<ManualPaymentOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [rejectModal, setRejectModal] = useState<{ order: ManualPaymentOrder } | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [screenshotPreviewUrl, setScreenshotPreviewUrl] = useState<string | null>(null);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = { page: currentPage, limit: 20 };
      if (paymentMethodFilter !== 'all') params.paymentMethod = paymentMethodFilter;
      if (paymentStatusFilter !== 'all') params.paymentStatus = paymentStatusFilter;
      const response = await api.get('/admin/orders/manual-payments', { params });
      setOrders(response.data.data || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading manual payments:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [currentPage, paymentMethodFilter, paymentStatusFilter]);

  const handleApprove = async (orderId: string) => {
    try {
      setSubmitting(true);
      await api.post(`/admin/orders/${orderId}/payment/approve`);
      await loadOrders();
      setRejectModal(null);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      alert(ax.response?.data?.message || t('manualPayments.failedToApprove'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    try {
      setSubmitting(true);
      await api.post(`/admin/orders/${rejectModal.order._id}/payment/reject`, {
        rejectionReason: rejectionReason || undefined,
      });
      await loadOrders();
      setRejectModal(null);
      setRejectionReason('');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      alert(ax.response?.data?.message || t('manualPayments.failedToReject'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPaymentStatusLabel = (status: string) => {
    const key = PAYMENT_STATUS_KEYS[status];
    return key ? t(`manualPayments.${key}`) : status;
  };

  if (loading && orders.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange"></div>
        <p className="mt-4 text-gray-600">{t('manualPayments.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-lg shadow-md p-4">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
          <BanknotesIcon className="w-6 h-6 text-primary-orange" />
          {t('manualPayments.title')}
        </h2>
        <div className={`flex flex-wrap gap-4 mb-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <select
            value={paymentMethodFilter}
            onChange={(e) => setPaymentMethodFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-orange"
          >
            <option value="all">{t('manualPayments.allMethods')}</option>
            <option value="instapay">InstaPay</option>
            <option value="wallet">{t('manualPayments.mobileWallet')}</option>
          </select>
          <select
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-orange"
          >
            <option value="all">{t('manualPayments.allStatuses')}</option>
            <option value="pending_upload">{t('manualPayments.pendingUpload')}</option>
            <option value="awaiting_admin_review">{t('manualPayments.awaitingReview')}</option>
            <option value="approved">{t('manualPayments.approved')}</option>
            <option value="rejected">{t('manualPayments.rejected')}</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manualPayments.orderId')}</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manualPayments.user')}</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manualPayments.amount')}</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manualPayments.method')}</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manualPayments.screenshot')}</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manualPayments.uploadedAt')}</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manualPayments.paymentStatus')}</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    {t('manualPayments.noOrders')}
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order._id}>
                    <td className="px-4 py-2 text-sm text-gray-900">{order._id.slice(-8)}</td>
                    <td className="px-4 py-2 text-sm">
                      {order.userId && typeof order.userId === 'object'
                        ? (order.userId as { name?: string; email?: string }).name || (order.userId as { email?: string }).email
                        : '-'}
                    </td>
                    <td className="px-4 py-2 text-sm font-medium">{order.totalPriceEGP} EGP</td>
                    <td className="px-4 py-2 text-sm">{order.paymentMethod === 'instapay' ? 'InstaPay' : t('manualPayments.mobileWallet')}</td>
                    <td className="px-4 py-2">
                      {order.paymentScreenshotUrl ? (
                        <button
                          type="button"
                          onClick={() => setScreenshotPreviewUrl(order.paymentScreenshotUrl!)}
                          className="text-primary-orange hover:underline text-sm text-left"
                        >
                          {t('manualPayments.viewScreenshot')}
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">{formatDate(order.createdAt)}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          order.paymentStatus === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : order.paymentStatus === 'rejected'
                              ? 'bg-red-100 text-red-800'
                              : order.paymentStatus === 'awaiting_admin_review'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {getPaymentStatusLabel(order.paymentStatus)}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {order.paymentStatus === 'awaiting_admin_review' ? (
                        <div className={`flex gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
                          <button
                            onClick={() => handleApprove(order._id)}
                            disabled={submitting}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg disabled:opacity-50"
                          >
                            {t('manualPayments.approve')}
                          </button>
                          <button
                            onClick={() => setRejectModal({ order })}
                            disabled={submitting}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg disabled:opacity-50"
                          >
                            {t('manualPayments.reject')}
                          </button>
                        </div>
                      ) : order.paymentStatus === 'approved' ? (
                        <div className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
                          <span className="text-green-600 text-sm">{t('manualPayments.approved')}</span>
                          <button
                            onClick={() => setRejectModal({ order })}
                            disabled={submitting}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg disabled:opacity-50"
                          >
                            {t('manualPayments.reject')}
                          </button>
                        </div>
                      ) : order.paymentStatus === 'pending_upload' ? (
                        <span className="text-gray-500 text-sm">{t('manualPayments.waitingForScreenshot')}</span>
                      ) : order.paymentStatus === 'rejected' ? (
                        <div className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
                          <span className="text-red-600 text-sm">{t('manualPayments.rejected')}</span>
                          <button
                            onClick={() => handleApprove(order._id)}
                            disabled={submitting}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg disabled:opacity-50"
                          >
                            {t('manualPayments.approve')}
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className={`flex justify-between items-center mt-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <p className="text-sm text-gray-600">
              {t('manualPayments.pageInfo', { page: currentPage, totalPages, total })}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="px-4 py-2 border rounded-lg disabled:opacity-50"
              >
                {t('common.previous')}
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="px-4 py-2 border rounded-lg disabled:opacity-50"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {screenshotPreviewUrl && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={t('manualPayments.viewScreenshot')}
          onClick={() => setScreenshotPreviewUrl(null)}
        >
          <div className="relative max-w-4xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setScreenshotPreviewUrl(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-200 text-2xl font-bold leading-none"
              aria-label={t('common.cancel')}
            >
              ×
            </button>
            <img
              src={screenshotPreviewUrl}
              alt="Payment screenshot"
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg shadow-xl"
            />
          </div>
        </div>
      )}

      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-2">{t('manualPayments.rejectTitle')}</h3>
            <p className="text-sm text-gray-600 mb-4">{t('manualPayments.rejectDescription')}</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder={t('manualPayments.rejectionReasonPlaceholder')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 min-h-[80px]"
              rows={3}
            />
            <div className={`flex gap-2 justify-end ${isRtl ? 'flex-row-reverse' : ''}`}>
              <button
                onClick={() => { setRejectModal(null); setRejectionReason(''); }}
                className="px-4 py-2 border rounded-lg"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleReject}
                disabled={submitting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg disabled:opacity-50"
              >
                {submitting ? t('common.saving') : t('manualPayments.reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
